import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialSkipListState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { createAsyncThunk, PayloadAction, createSlice } from "@reduxjs/toolkit";

const { skipListEndPoint } = endPoints;

export const fetchAllSkippedContacts = createAsyncThunk(
  "skippedContacts/fetchAllSkippedContacts",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue },
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: skipListEndPoint,
      searchParams: { ...searchParamsObj },
    });
    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response?.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  },
);

export const addSkippedContact = createAsyncThunk(
  "skippedContacts/addSkippedContact",
  async (newContact: any, { rejectWithValue }) => {
    try {
      // In new contact there is an array of phone numbers, we need to create a separate contact for each phone number
      const results = [];
      for (const phone of newContact?.phone_number) {
        if (!phone) {
          continue;
        }
        try {
          const response: AxiosResponse = await crudService.create(
            `${skipListEndPoint}/`,
            { ...newContact, phone_number: phone },
          );

          if (response.status === 201) {
            NotificationManager.success(phone + " Added Successfully.");
            results.push(response.data);
          }
        } catch (error) {
          const errorMessage = getErrorMessage(error);

          NotificationManager.error(errorMessage);
          //  rejectWithValue(error.message);
          continue;
        }
      }
      return results;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  },
);

export const deleteSkippedContact = createAsyncThunk(
  "skippedContacts/deleteSkippedContact",
  async ({ contactId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        skipListEndPoint,
        contactId,
      );

      if (response.status === 204) {
        NotificationManager.success("Skipped Contact Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  },
);

export const updateSkippedContact = createAsyncThunk(
  "skippedContacts/updateSkippedContact",
  async ({ updatedContact, contactId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        skipListEndPoint,
        contactId,
        {
          ...updatedContact,
          id: contactId,
          phone_number: updatedContact?.phone_number[0],
        },
      );
      if (response.status === 204) {
        NotificationManager.success("Skipped Contact updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  },
);

const skippedContactSlice = createSlice({
  name: "skippedContacts",
  initialState: initialSkipListState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all skipped contacts
    builder
      .addCase(fetchAllSkippedContacts.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllSkippedContacts.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.skipList = action.payload.objects;
          state.meta = action.payload?.meta;
        },
      )
      .addCase(
        fetchAllSkippedContacts.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.skipList = [];
          state.meta = defaultMetaObj;
        },
      );

    // add skipped contact
    builder
      .addCase(addSkippedContact.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addSkippedContact.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        },
      )
      .addCase(addSkippedContact.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // delete Skipped Contact
    builder
      .addCase(deleteSkippedContact.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deleteSkippedContact.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        },
      )
      .addCase(
        deleteSkippedContact.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        },
      );

    //   update Skipped Contact
    builder
      .addCase(updateSkippedContact.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateSkippedContact.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        },
      )
      .addCase(
        updateSkippedContact.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        },
      );
  },
});

export const skippedContactReducer = skippedContactSlice.reducer;
